import { useEffect, useState, useMemo } from 'react'
import { useApi } from '../hooks/useApi'
import type { ProfileRow } from '../types'
import Card from '../components/Card'

const FROM_EMAIL = import.meta.env.VITE_ICA_RESEND_FROM_EMAIL || 'reminders@mail.invoicechase.aiagents-labs.com'

function buildPreview(senderName: string | null, replyTo: string | null): { from: string; repliesTo: string } {
  const name = (senderName || '').trim()
  const from = name
    ? `${name} via Invoice Chase <${FROM_EMAIL}>`
    : `Invoice Chase <${FROM_EMAIL}>`
  return { from, repliesTo: replyTo || 'Not configured' }
}

export default function Settings() {
  const api = useApi()
  const [profile, setProfile] = useState<ProfileRow | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [saveSuccess, setSaveSuccess] = useState(false)
  const [testLoading, setTestLoading] = useState(false)
  const [testResult, setTestResult] = useState<string | null>(null)
  const [testError, setTestError] = useState<string | null>(null)

  useEffect(() => {
    api
      .getMe()
      .then((data) => setProfile(data.profile))
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false))
  }, [])

  async function handleSave(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault()
    setSaveError(null)
    setSaveSuccess(false)
    setSaving(true)

    const form = e.currentTarget
    const formData = new FormData(form)

    try {
      const data = await api.updateMe({
        name: formData.get('name'),
        sender_name: formData.get('sender_name'),
        sender_email: formData.get('sender_email'),
        reply_to_email: formData.get('reply_to_email'),
      })
      setProfile(data.profile)
      setSaveSuccess(true)
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to save')
    } finally {
      setSaving(false)
    }
  }

  async function handleSendTest() {
    if (testLoading) return
    setTestLoading(true)
    setTestResult(null)
    setTestError(null)
    try {
      await api.sendTestEmail()
      setTestResult('Test email sent successfully. Check your inbox.')
    } catch (err) {
      setTestError(err instanceof Error ? err.message : 'Failed to send test email')
    } finally {
      setTestLoading(false)
    }
  }

  const preview = useMemo(() => {
    if (!profile) return null
    return buildPreview(
      profile.sender_name,
      profile.reply_to_email || profile.sender_email || profile.email
    )
  }, [profile])

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-ica-border border-t-ica-accent" />
      </div>
    )
  }

  if (error || !profile) {
    return <Card className="border-ica-red/20 bg-ica-red/5 p-4 text-sm text-ica-red">{error || 'Failed to load profile'}</Card>
  }

  return (
    <div className="mx-auto max-w-xl space-y-8">
      <h1 className="text-2xl font-bold tracking-tight">Settings</h1>

      <Card elevated className="space-y-6">
        <div>
          <h2 className="text-lg font-semibold">Sender identity</h2>
          <p className="text-sm text-ica-text-muted">These details appear in reminder emails sent to your clients.</p>
        </div>

        <Card className="border-ica-amber/20 bg-ica-amber/5">
          <p className="text-xs text-ica-amber">
            Emails are sent from our verified domain. Replies go to your reply-to email.
          </p>
        </Card>

        {preview && (
          <Card className="space-y-2 border-ica-border/60 bg-ica-elevated/60">
            <p className="text-xs font-medium text-ica-text-muted">Preview</p>
            <div className="text-sm text-ica-text">
              <span className="text-ica-text-muted">From:</span> {preview.from}
            </div>
            <div className="text-sm text-ica-text">
              <span className="text-ica-text-muted">Replies go to:</span> {preview.repliesTo}
            </div>
          </Card>
        )}

        <form className="space-y-4" onSubmit={handleSave}>
          {saveSuccess && <div className="rounded-lg bg-ica-green/10 px-3 py-2 text-sm text-ica-green">Settings saved.</div>}
          {saveError && <div className="rounded-lg bg-ica-red/10 px-3 py-2 text-sm text-ica-red">{saveError}</div>}

          <div>
            <label className="mb-1.5 block text-xs font-medium text-ica-text-muted">Your name</label>
            <input
              name="name"
              defaultValue={profile.name || ''}
              className="w-full rounded-xl border border-ica-border bg-ica-elevated px-3 py-2.5 text-sm text-ica-text outline-none transition-colors focus:border-ica-accent/50"
            />
          </div>
          <div>
            <label className="mb-1.5 block text-xs font-medium text-ica-text-muted">Sender name</label>
            <input
              name="sender_name"
              defaultValue={profile.sender_name || ''}
              placeholder="e.g., Sarah Design"
              className="w-full rounded-xl border border-ica-border bg-ica-elevated px-3 py-2.5 text-sm text-ica-text outline-none transition-colors focus:border-ica-accent/50"
            />
            <p className="mt-1 text-xs text-ica-text-muted">The name shown in the From field of reminder emails.</p>
          </div>
          <div>
            <label className="mb-1.5 block text-xs font-medium text-ica-text-muted">Reply-to email</label>
            <input
              name="reply_to_email"
              type="email"
              defaultValue={profile.reply_to_email || ''}
              placeholder="sarah@example.com"
              className="w-full rounded-xl border border-ica-border bg-ica-elevated px-3 py-2.5 text-sm text-ica-text outline-none transition-colors focus:border-ica-accent/50"
            />
            <p className="mt-1 text-xs text-ica-text-muted">Client replies go here. Falls back to sender email, then your account email.</p>
          </div>
          <div>
            <label className="mb-1.5 block text-xs font-medium text-ica-text-muted">Sender email</label>
            <input
              name="sender_email"
              type="email"
              defaultValue={profile.sender_email || ''}
              placeholder="sarah@example.com"
              className="w-full rounded-xl border border-ica-border bg-ica-elevated px-3 py-2.5 text-sm text-ica-text outline-none transition-colors focus:border-ica-accent/50"
            />
            <p className="mt-1 text-xs text-ica-text-muted">Fallback reply-to if no dedicated reply-to is set.</p>
          </div>

          <button
            type="submit"
            disabled={saving}
            className="rounded-xl bg-ica-accent px-4 py-2.5 text-sm font-semibold text-ica-accent-text transition-opacity hover:opacity-90 disabled:opacity-40"
          >
            {saving ? 'Saving...' : 'Save settings'}
          </button>
        </form>
      </Card>

      <Card elevated className="space-y-4">
        <div>
          <h2 className="text-lg font-semibold">Email integration</h2>
          <p className="text-sm text-ica-text-muted">Send a test email to yourself to verify your sending configuration.</p>
        </div>

        {testResult && <div className="rounded-lg bg-ica-green/10 px-3 py-2 text-sm text-ica-green">{testResult}</div>}
        {testError && <div className="rounded-lg bg-ica-red/10 px-3 py-2 text-sm text-ica-red">{testError}</div>}

        <button
          onClick={handleSendTest}
          disabled={testLoading}
          className="rounded-xl bg-ica-accent px-4 py-2.5 text-sm font-semibold text-ica-accent-text transition-opacity hover:opacity-90 disabled:opacity-40"
        >
          {testLoading ? 'Sending...' : 'Send test email'}
        </button>
      </Card>
    </div>
  )
}
